import { Fancybox } from "@fancyapps/ui";
import { locales } from "../locales"
import { stateClasses as formStateClasses } from "../forms"
import { getLocaleMsg } from "../utils/getLocaleMsg"
import { dispatchContentLoaded } from "../generic/eventing";
import { getNodeIndex } from "../utils/getNodeIndex"
import { isUrl } from "../utils/isUrl"
import { parseJSON } from "../utils/parseJSON"
import { getAttr } from "../utils/getAttr"
import { bubble } from "../utils/bubble"
import { isEmptyObj } from "../utils/isEmptyObj"
import { getLastFromNodeList } from "../utils/getLastFromNodeList"
import { getFormDataFromObj } from "../utils/getFormDataFromObj"
import { makeRequest } from "../utils/makeRequest";
import { getCurrentLang } from "../utils/getCurrentLang"
import { wait } from "../utils/wait";

export class Modals {

  static selectors = {
    modal: "[data-js-modal]",
    errorText: "[data-js-modal-error-text]",
    open: "[data-js-modal-open]",
    close: "[data-js-modal-close]",
    submit: "[data-js-modal-submit]",
    cancel: "[data-js-modal-cancel]",
    defaultAnchors: {
      success: "#modal-success",
      error: "#modal-error"
    },
    gallery: {
      item: "[data-js-gallery-item]",
      container: "[data-js-gallery-container]",
    }
  }

  /**
   * Вёрстка элементов интерфейса модальных окон
   */
  static layouts =  {
    closeButton: `<svg class="icon icon--medium-plus">
                    <use href="#icon-close"></use>
                  </svg>`,
    nextButton: `<svg class="icon icon--large">
                    <use href="#icon-page-next"></use>
                  </svg>`,
    prevButton: `<svg class="icon icon--large">
                    <use href="#icon-page-prev"></use>
                 </svg>`
  }

  /**
   * Конфигурация по умолчанию (см. api fancybox)
   */
  static defaultCfg = {
    animated: false,
    Thumbs: false,
    Image: false,
    hideScrollbar: true,
    infinite: false,
    on: {
      reveal: (instance, slide) => Modals.handleModalReveal(instance, slide),
      close: (instance) => Modals.handleModalClose(instance)
    },
    l10n: Object.entries(locales.modals).map(([ key, value ]) => { return { [key]: value[getCurrentLang()] }; }).reduce((a, b) => Object.assign(a, b), {}),
    template: {
      closeButton: Modals.layouts.closeButton
    },
    trapFocus: true,
    autoFocus: false,
    placeFocusBack: false,
    preload: false,
    modal: true,
    touch: false,
    fullScreen: false,
    defaultActionCfg: {
      redirectAfterClose: false,
      reloadAfterClose: false,
      bubbleAfterClose: false,
      afterConfirm: (instance, event) => Modals.handleModalConfirm(instance, event),
      afterCancel: (instance, event) => Modals.handleModalCancel(instance, event)
    },
    draggable: false,
    lockAxis: true,
    method: "GET",
    data: {},
    showClass: "modal-show",
    hideClass: "modal-hide"
  };

  constructor() {
    this.bindEvents();
  }

  /**
   * Получение конфигурации открытого модального окна
   * @param modal{HTMLElement} - модальное окно
   * @returns Object
   */
  static getActionCfg(modal) {
    return {
      ...Modals.defaultCfg.defaultActionCfg,
      ...parseJSON(modal.getAttribute(getAttr(Modals.selectors.modal)))
    }
  }

  /**
   * Получение конфигурации открываемого модального окна
   * @param handler{HTMLElement} - кнопка или ссылка
   * @return Object
   */
  static getCfg(handler) {
    return {
      handler,
      src: handler.hasAttribute("href") ? handler.getAttribute("href") : "",
      isLockHandler: true,
      ...Modals.defaultCfg,
      ...parseJSON(handler.getAttribute(getAttr(Modals.selectors.open)))
    }
  }

  /**
   * Стандартная функция-обработчик подтверждения действия
   * @param instance{Function} - Fancybox instance
   * @param event
   */
  static handleModalConfirm(instance, event) {
    event.preventDefault();
    Modals.closeModal(instance);
  }

  /**
   * Стандартная функция-обработчик отмены действия
   * @param instance{Function} - Fancybox instance
   * @param event
   */
  static handleModalCancel(instance, event) {
    event.preventDefault();
    Modals.closeModal(instance);
  }

  /**
   * Стандартная функция-обработчик появления слайда
   * @param instance{Function} - Fancybox instance
   * @param slide{Object} - текущий слайд
   */
  static handleModalReveal(instance, slide) {
    const modal = slide.el.querySelector(Modals.selectors.modal);
    if(modal) {
      dispatchContentLoaded({ content: modal });
    }
  }

  /**
   * Получение содержимого модального окна из fancybox instance
   * @param instance{Function} - Fancybox instance
   * @returns {HTMLElement||null}
   */
  static getModalContent(instance) {
    return instance.getSlide().el.querySelector(Modals.selectors.modal);
  }

  /**
   * Стандартная функция-обработчик закрытия модального окна
   * @param instance{Function} - Fancybox instance
   */
  static handleModalClose(instance) {
    const modal = Modals.getModalContent(instance);
    if(modal) {
      const { bubbleAfterClose, redirectAfterClose, reloadAfterClose } = Modals.getActionCfg(modal);
      if(bubbleAfterClose) {
        bubble(document, bubbleAfterClose.toString(), modal);
      }
      if(typeof redirectAfterClose === "string") {
        window.location.href = redirectAfterClose.toString();
      } else if(reloadAfterClose) {
        window.location.reload();
      } else {
        const { options } = instance;
        const { isLockHandler, handler } = options;
        if(isLockHandler && handler) {
          Modals.setModalHandlerLock(handler, false);
        }
      }
    }
  }

  /**
   * Закрытие всех активных вызовов fancybox
   */
  static closeAllModals() {
    Fancybox.close(true);
  }

  /**
   * Закрытие указанного активного вызова fancybox
   * @param instance{Function} - Fancybox instance
   */
  static closeModal(instance = Modals.getActiveInstance()) {
    if(instance) {
      instance.close();
    }
  }

  static openInlineModal(cfg) {
    const { src } = cfg;
    const el = document.querySelector(src);
    if(el) {
      const isTpl = el.tagName.toLowerCase() === "template";
      Fancybox.show([ {
        src: isTpl ? el.innerHTML : src,
        type: isTpl ? "html" : "inline"
      } ], {
        ...Modals.defaultCfg,
        ...cfg,
      });
    } else {
      Modals.openErrorModal();
    }
  }

  static openConfirmModal(cfg, confirmFn, cancelFn) {
    Modals.openInlineModal({
      src: "#modal-confirm",
      defaultActionCfg: {
        ...Modals.defaultCfg.defaultActionCfg,
        afterConfirm: (instance, e) => {
          e.preventDefault();
          if(typeof confirmFn === "function") {
            confirmFn(instance, e);
          } else {
            Modals.handleModalConfirm(instance, e);
          }
        },
        afterCancel: (instance, e) => {
          e.preventDefault();
          if(typeof cancelFn === "function") {
            cancelFn(instance, e);
          } else {
            Modals.handleModalCancel(instance, e);
          }
        }
      },
      ...cfg
    })
  }

  static openHTMLModal(cfg) {
    const { src } = cfg;
    Fancybox.show([ { src, type: "html" } ], {
      ...Modals.defaultCfg,
      ...cfg,
    });
  }

  static handleRequestError(status = 404, callback) {
    let msg = getLocaleMsg("ERROR");
    switch (status) {
      case 408:
        msg = getLocaleMsg("TIMEOUT");
        break;
      case 404:
        msg = getLocaleMsg("AJAX_NOT_FOUND");
        break;
      case 403:
        msg = getLocaleMsg("AJAX_FORBIDDEN");
        break;
    }
    Modals.openErrorModal(msg);
    if(typeof callback === "function") {
      callback();
    }
  }

  static openAjaxModal(cfg) {
    const { src = "", method = "GET", data = {}, on } = cfg;
    if(src) {
      const body = getFormDataFromObj(data);
      const handleError = () => {
        if(typeof on.error === "function") {
          on.error();
        }
      }
      makeRequest({ url: src, type: "text", method, body }).then((data) => {
        if(cfg.data) {
          delete cfg.data;
        }
        const json = parseJSON(data);
        if(isEmptyObj(json)) {
          Modals.openHTMLModal({
            ...cfg,
            src: data
          });
        } else if(json.errors) {
          Modals.openErrorModal(json.errors);
          handleError();
        }
      }, (errorCode) => Modals.handleRequestError(errorCode, handleError))
    } else {
      console.error("Can't open ajax modal with invalid cfg:", cfg);
    }
  }

  static setErrorText(errorText, slide, isReset = false) {
    const context = (typeof slide.el === "undefined") ? slide : slide.el;
    const errorBlock = context.querySelector(Modals.selectors.errorText);
    const modalContent = context.querySelector(".fancybox__content");
    if(errorBlock && errorText) {
      errorBlock.innerHTML = (isReset) ? getLocaleMsg("ERROR") : (Array.isArray(errorText) ? errorText.join("<br />") : errorText);
    } else if (modalContent && errorText) {
      modalContent.innerHTML = `<div data-js-modal-error-text>${errorText}</div>`
    }
  }

  static openErrorModal(errorText = getLocaleMsg("ERROR"), isCloseOthers = true, debugInfo) {
    if(isCloseOthers) {
      Modals.closeAllModals();
    }
    Modals.openInlineModal({
      src: Modals.selectors.defaultAnchors.error,
      on: {
        ...Modals.defaultCfg.on,
        reveal: (instance, slide) => Modals.setErrorText(errorText, slide),
        destroy: () => Modals.setErrorText(errorText, document.querySelector(Modals.selectors.defaultAnchors.error), true),
        close: (instance) => Modals.handleModalClose(instance)
      }
    });
    if(debugInfo) {
      console.debug(debugInfo);
    }
  }

  static getActiveInstance() {
    let instance = Fancybox.getInstance();
    const modals = document.querySelectorAll('[id^="fancybox-"][aria-modal]');
    if(modals.length && !instance) {
      const lastModal = getLastFromNodeList(modals);
      instance = lastModal.Fancybox || null;
    }
    return instance;
  }

  static setModalHandlerLock(handler, isLock = true) {
    if(handler) {
      (isLock) ? handler.classList.add(formStateClasses.disabled) : handler.classList.remove(formStateClasses.disabled);
      if(handler.matches("button") || handler.matches("input")) {
        handler.disabled = isLock;
      }
    }
  }

  handleOpenClick(e) {
    e.preventDefault();
    const cfg = Modals.getCfg(e.target);
    const { src, isLockHandler, handler, on }  = cfg;
    const setLock = (isLock = true) => {
      if(handler && isLockHandler) {
        Modals.setModalHandlerLock(handler, isLock);
      }
    }
    if(typeof on.error === "undefined") {
      on.error = () => setLock(false);
    }
    if(src) {
      setLock();
      switch(true) {
        case isUrl(src) || src.startsWith("/") || src.startsWith("."):
          Modals.openAjaxModal(cfg);
          break;
        case src.startsWith("#"):
          Modals.openInlineModal(cfg);
          break;
        default:
          Modals.openHTMLModal(cfg);
          break;
      }
    } else {
      throw new Error("No src for modal");
    }
  }

  handleCloseClick(e) {
    e.preventDefault();
    wait(50).then(() => e.target.blur());
    Modals.closeModal();
  }

  handleConfirmClick(e) {
    e.preventDefault();
    const instance = Modals.getActiveInstance();
    if(typeof instance.options.defaultActionCfg.afterConfirm === "function") {
      instance.options.defaultActionCfg.afterConfirm(instance, e);
    }
  }

  handleCancelClick(e) {
    e.preventDefault();
    const instance = Modals.getActiveInstance();
    if(typeof instance.options.defaultActionCfg.afterCancel === "function") {
      instance.options.defaultActionCfg.afterCancel(instance, e);
    }
  }

  handleMouseDown(e) {
    const { target, button } = e;
    if(button === 0) {
      const isMatch = (selector) => target.matches(selector);
      const { open, close, cancel, submit } = Modals.selectors;
      switch(true) {
        case isMatch(open):
          this.handleOpenClick(e);
          break;
        case isMatch(close):
          this.handleCloseClick(e);
          break;
        case isMatch(cancel):
          this.handleCancelClick(e);
          break;
        case isMatch(submit):
          this.handleConfirmClick(e);
          break;
        default:
          break;
      }
    }
  }

  bindEvents() {
    document.addEventListener("mousedown", (e) => this.handleMouseDown(e))
  }
}

export class Gallery extends Modals {

  static defaultCfg = {
    ...Modals.defaultCfg,
    animated: true,
    Thumbs: false,
    Image: {
      animateThumbnail: false,
    },
    closeButton: "outside",
    on: false,
    trapFocus: true,
    autoFocus: false,
    placeFocusBack: false,
    infinite: false,
    defaultType: "image"
  }

  constructor() {
    super();
  }

  bindEvents() {
    document.addEventListener("click", (e) => this.handleClick(e));
  }

  static getCfg(container, extraParams = {}) {
    return {
      ...Gallery.defaultCfg,
      ...parseJSON(container.getAttribute(getAttr(Modals.selectors.gallery.container))),
      ...extraParams
    }
  }

  static open(items = [], cfg = {}, isCloseOther = true) {
    if(isCloseOther) {
      Fancybox.close(true);
    }
    Fancybox.show(items, cfg);
  }

  static getItems(container = document.body) {
    return [ ...container.querySelectorAll(Modals.selectors.gallery.item) ].map(node => {
      return {
        src: node.getAttribute("href"),
        type: Gallery.defaultCfg.defaultType,
        ...parseJSON(node.getAttribute(getAttr(Modals.selectors.gallery.item)))
      }
    });
  }

  // static getStartIndex(el) {
  //   const slide = el.closest(SlidersCollection.selectors.slide);
  //   return getNodeIndex(slide ? slide : el);
  // }

  handleClick(e) {
    const { target } = e;
    if(target.matches(Modals.selectors.gallery.item) && target.hasAttribute("href")) {
      e.preventDefault();
      const container = target.closest(Modals.selectors.gallery.container);
      if(container) {
        const itemsArray = Gallery.getItems(container);
        const cfg = Gallery.getCfg(container, { startIndex: 0 });
        Gallery.open(itemsArray, cfg);
      } else {
        throw new Error(`No closest el ${Modals.selectors.gallery.container} for this item`);
      }
    }
  }

}

